import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as api from '../services/api';

const ACCENT = '#025c4f';
const ACCENT_LIGHT = '#dbf8f4';
const BLUE = '#2e86de';
const ORANGE = '#e67e22';
const RED = '#dc3545';
const GRAY = '#6c757d';

const pillBase = {
  padding: '5px 12px', borderRadius: '20px', fontSize: '12px',
  fontWeight: 600, cursor: 'pointer', border: '1px solid #adb5bd',
  backgroundColor: '#fff', color: '#495057', transition: 'all 0.2s',
  display: 'inline-block',
};
const pillActive = (color) => ({
  ...pillBase, border: '2px solid ' + color, backgroundColor: color,
  color: '#fff', boxShadow: '0 2px 8px ' + color + '40',
});
const sectionStyle = {
  marginBottom: '18px', padding: '16px 20px', background: '#f8f9fa',
  borderRadius: '10px', border: '1px solid #dee2e6',
};
const labelStyle = {
  display: 'block', marginBottom: '6px', fontWeight: '600',
  fontSize: '14px', color: '#333',
};
const inputStyle = {
  padding: '8px 14px', fontSize: '14px', borderRadius: '8px',
  border: '1px solid #ccc', width: '100%', boxSizing: 'border-box', outline: 'none',
};
const btnPrimary = {
  padding: '10px 24px', fontSize: '14px', fontWeight: 600,
  borderRadius: '8px', border: 'none', color: '#fff',
  cursor: 'pointer', transition: 'all 0.2s',
};

function PhenotypeSearch({ crop }) {
  const { getAccessTokenSilently } = useAuth0();

  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  const [radFilter, setRadFilter] = useState(null);
  const [yearFilter, setYearFilter] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [years, setYears] = useState([]);

  const [allMarkers, setAllMarkers] = useState([]);
  const [markerSearch, setMarkerSearch] = useState('');
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [markerDropdownOpen, setMarkerDropdownOpen] = useState(false);
  const markerRef = useRef(null);

  const [allCultivars, setAllCultivars] = useState([]);
  const [cultivarSearch, setCultivarSearch] = useState('');
  const [selectedCultivars, setSelectedCultivars] = useState([]);
  const [cultivarDropdownOpen, setCultivarDropdownOpen] = useState(false);
  const cultivarRef = useRef(null);

  const [markerFile, setMarkerFile] = useState(null);
  const [cultivarFile, setCultivarFile] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [legacyResults, setLegacyResults] = useState(null);
  const [retrieving, setRetrieving] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load tables
  useEffect(() => {
    (async () => {
      try {
        setTableLoading(true);
        const token = await getAccessTokenSilently();
        const data = await api.getMasterTables(token, crop);
        setTables(data.tables || []);
      } catch (e) {
        console.error(e);
        setErrorMessage('Failed to load tables.');
      } finally {
        setTableLoading(false);
      }
    })();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!selectedTable) return;
    clearAllFilters(false);
    loadTableMeta();
  }, [selectedTable]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTableMeta = async () => {
    setMetaLoading(true);
    try {
      const [mkRes, cvRes, yrRes] = await Promise.all([
        api.getSnpMarkers(selectedTable),
        api.getSampleCultivars(selectedTable),
        api.getAvailableYears(selectedTable),
      ]);
      setAllMarkers(mkRes.markers || []);
      setAllCultivars(cvRes.cultivars || []);
      setYears(yrRes.years || []);
    } catch (e) {
      console.error('Error loading table metadata:', e);
      setAllMarkers([]);
      setAllCultivars([]);
      setYears([]);
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (markerRef.current && !markerRef.current.contains(e.target)) setMarkerDropdownOpen(false);
      if (cultivarRef.current && !cultivarRef.current.contains(e.target)) setCultivarDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isSampleOriented = allMarkers.length > 0;

  const clearAllFilters = (clearTable) => {
    setRadFilter(null);
    setYearFilter('');
    setNameSearch('');
    setMarkerSearch('');
    setSelectedMarkers([]);
    setCultivarSearch('');
    setSelectedCultivars([]);
    setMarkerFile(null);
    setCultivarFile(null);
    setPreview(null);
    setLegacyResults(null);
    setErrorMessage('');
    setSuccessMsg('');
    if (clearTable) setSelectedTable('');
  };

  const buildFilters = () => ({
    rad_filter: radFilter,
    year_filter: yearFilter || null,
    cultivar_names: selectedCultivars,
    marker_names: selectedMarkers,
    name_search: nameSearch,
    include_metadata: true,
  });

  const activeFilterCount = () => {
    let n = 0;
    if (radFilter) n++;
    if (yearFilter) n++;
    if (nameSearch.trim()) n++;
    if (selectedMarkers.length) n++;
    if (selectedCultivars.length) n++;
    return n;
  };

  const filteredMarkers = markerSearch.trim()
    ? allMarkers.filter(m => m.toLowerCase().includes(markerSearch.toLowerCase()))
    : allMarkers;

  const filteredCultivars = (() => {
    let list = allCultivars;
    if (radFilter) list = list.filter(c => c.rad === radFilter);
    if (yearFilter) list = list.filter(c => c.year === yearFilter);
    if (cultivarSearch.trim()) {
      const s = cultivarSearch.trim().toLowerCase();
      list = list.filter(c =>
        c.genovix_name.toLowerCase().includes(s) ||
        c.alias_name.toLowerCase().includes(s)
      );
    }
    return list;
  })();

  const handlePreview = async () => {
    if (!selectedTable) { setErrorMessage('Select a table first'); return; }
    try {
      setLoading(true); setErrorMessage(''); setSuccessMsg(''); setLegacyResults(null);
      const data = await api.previewSubset(selectedTable, buildFilters());
      setPreview(data);
    } catch (e) {
      console.error(e);
      setErrorMessage((e.response && e.response.data && e.response.data.detail) || 'Error loading preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedTable) { setErrorMessage('Select a table first'); return; }
    try {
      setDownloading(true); setErrorMessage(''); setSuccessMsg('');
      const response = await api.downloadSubset(selectedTable, buildFilters());
      const disposition = response.headers['content-disposition'];
      let filename = selectedTable + '_subset.csv';
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMsg('Downloaded ' + filename);
    } catch (e) {
      console.error(e);
      setErrorMessage((e.response && e.response.data && e.response.data.detail) || 'Error downloading subset');
    } finally {
      setDownloading(false);
    }
  };

  const handleLegacyRetrieve = async () => {
    if (!selectedTable) { setErrorMessage('Select a table first'); return; }
    try {
      setRetrieving(true); setErrorMessage(''); setPreview(null);
      const token = await getAccessTokenSilently();
      const response = await api.retrieveSubset(token, selectedTable, markerFile, cultivarFile);
      setLegacyResults(response);
    } catch (e) {
      console.error(e);
      setErrorMessage((e.response && e.response.data && e.response.data.detail) || e.message || 'Error retrieving data');
    } finally {
      setRetrieving(false);
    }
  };

  const exportLegacyCSV = () => {
    if (!legacyResults || !legacyResults.data || !legacyResults.data.length) return;
    const headers = legacyResults.columns.join(';');
    const rows = legacyResults.data.map(function(row) {
      return legacyResults.columns.map(function(col) {
        const v = row[col];
        if (v === null || v === undefined) return '';
        const s = String(v);
        if (s.includes(';') || s.includes('"')) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      }).join(';');
    });
    const blob = new Blob([[headers].concat(rows).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = selectedTable + '_subset.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const toggleMarker = (m) => {
    setSelectedMarkers(function(prev) {
      return prev.includes(m) ? prev.filter(function(x) { return x !== m; }) : prev.concat([m]);
    });
  };
  const selectAllVisibleMarkers = () => {
    setSelectedMarkers(function(prev) {
      const s = new Set(prev);
      filteredMarkers.forEach(function(m) { s.add(m); });
      return Array.from(s);
    });
  };
  const clearSelectedMarkers = () => setSelectedMarkers([]);

  const toggleCultivar = (name) => {
    setSelectedCultivars(function(prev) {
      return prev.includes(name) ? prev.filter(function(x) { return x !== name; }) : prev.concat([name]);
    });
  };
  const selectAllVisibleCultivars = () => {
    setSelectedCultivars(function(prev) {
      const s = new Set(prev);
      filteredCultivars.forEach(function(c) { s.add(c.genovix_name); });
      return Array.from(s);
    });
  };
  const clearSelectedCultivars = () => setSelectedCultivars([]);

  const handleDeleteTable = async () => {
    if (!selectedTable) return;
    if (!window.confirm(`Delete table "${selectedTable}"?\n\nThis will permanently remove all data in this table. This cannot be undone.`)) return;
    try {
      setDeleting(true); setErrorMessage('');
      await api.deleteTable(selectedTable);
      setSuccessMsg(`Table "${selectedTable}" deleted successfully.`);
      setSelectedTable('');
      clearAllFilters(true);
      // Refresh table list
      const token = await getAccessTokenSilently();
      const data = await api.getMasterTables(token, crop);
      setTables(data.tables || []);
    } catch (e) {
      setErrorMessage((e.response && e.response.data && e.response.data.detail) || 'Failed to delete table');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '4px' }}>{'📊 Data Explorer'}</h2>
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Browse, search, filter, and download subsets of your genotype data.
      </p>

      {errorMessage && (
        <div style={{ padding: '12px 18px', marginBottom: '16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
          {errorMessage}
          <button onClick={() => setErrorMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: '#721c24', cursor: 'pointer', fontWeight: 700 }}>X</button>
        </div>
      )}
      {successMsg && (
        <div style={{ padding: '12px 18px', marginBottom: '16px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '8px', border: '1px solid #c3e6cb' }}>
          {successMsg}
          <button onClick={() => setSuccessMsg('')} style={{ float: 'right', background: 'none', border: 'none', color: '#155724', cursor: 'pointer', fontWeight: 700 }}>X</button>
        </div>
      )}

      {/* Table Selector */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Select Table</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {tableLoading ? <p>Loading tables...</p> : (
            <select value={selectedTable} onChange={function(e) { setSelectedTable(e.target.value); }}
              style={Object.assign({}, inputStyle, { maxWidth: '500px', flex: 1 })}>
              <option value="">-- Select a table --</option>
              {tables.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
            </select>
          )}
          {selectedTable && (
            <button
              onClick={handleDeleteTable}
              disabled={deleting}
              style={Object.assign({}, btnPrimary, { backgroundColor: RED, padding: '8px 16px', fontSize: '13px', opacity: deleting ? 0.6 : 1 })}
              title="Delete this table permanently"
            >
              {deleting ? '⏳ Deleting…' : '🗑️ Delete Table'}
            </button>
          )}
        </div>
      </div>

      {selectedTable && isSampleOriented && (
        <React.Fragment>
          {/* Row Type + Year + Name Search */}
          <div style={Object.assign({}, sectionStyle, { display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' })}>
            <div>
              <label style={labelStyle}>{'🌾 Row Type'}</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => setRadFilter(radFilter === '2' ? null : '2')}
                  style={radFilter === '2' ? pillActive(BLUE) : pillBase}>2-Row</button>
                <button onClick={() => setRadFilter(radFilter === '6' ? null : '6')}
                  style={radFilter === '6' ? pillActive(ORANGE) : pillBase}>6-Row</button>
                {radFilter && (
                  <button onClick={() => setRadFilter(null)}
                    style={Object.assign({}, pillBase, { fontSize: '11px', padding: '3px 8px' })} title="Show all">X</button>
                )}
              </div>
            </div>

            <div>
              <label style={labelStyle}>{'📅 Year'}</label>
              <select value={yearFilter} onChange={function(e) { setYearFilter(e.target.value); }}
                style={Object.assign({}, inputStyle, { width: '160px' })}>
                <option value="">All Years</option>
                {years.map(function(y) { return <option key={y} value={y}>{y}</option>; })}
              </select>
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={labelStyle}>{'🔍 Cultivar Name Search'}</label>
              <input type="text" value={nameSearch} onChange={function(e) { setNameSearch(e.target.value); }}
                placeholder="Search genovix_name or alias_name..." style={inputStyle} />
            </div>
          </div>

          {/* Marker Picker */}
          <div style={sectionStyle} ref={markerRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={Object.assign({}, labelStyle, { marginBottom: 0 })}>
                {'🧬 Markers (SNP Columns) '}
                <span style={{ fontWeight: 400, color: '#666', fontSize: '13px' }}>
                  {selectedMarkers.length > 0 ? selectedMarkers.length + ' selected' : allMarkers.length + ' available - leave empty for all'}
                </span>
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {selectedMarkers.length > 0 && (
                  <button onClick={clearSelectedMarkers}
                    style={Object.assign({}, pillBase, { fontSize: '11px', color: RED })}>{'Clear (' + selectedMarkers.length + ')'}</button>
                )}
                <button onClick={() => setMarkerDropdownOpen(!markerDropdownOpen)}
                  style={Object.assign({}, pillBase, { backgroundColor: ACCENT_LIGHT, color: ACCENT, border: '1px solid ' + ACCENT })}>
                  {markerDropdownOpen ? 'Close' : 'Select Markers'}
                </button>
              </div>
            </div>

            {selectedMarkers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                {selectedMarkers.slice(0, 30).map(function(m) {
                  return (
                    <span key={m} style={Object.assign({}, pillBase, { backgroundColor: ACCENT_LIGHT, color: ACCENT, border: '1px solid ' + ACCENT, display: 'inline-flex', alignItems: 'center', gap: '4px' })}>
                      {m} <span onClick={() => toggleMarker(m)} style={{ cursor: 'pointer', fontWeight: 700 }}>x</span>
                    </span>
                  );
                })}
                {selectedMarkers.length > 30 && <span style={Object.assign({}, pillBase, { backgroundColor: '#eee' })}>+{selectedMarkers.length - 30} more</span>}
              </div>
            )}

            {markerDropdownOpen && (
              <div style={{ border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', maxHeight: '300px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #eee', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="text" value={markerSearch} onChange={function(e) { setMarkerSearch(e.target.value); }}
                    placeholder="Search markers (e.g. BK_01)..." style={Object.assign({}, inputStyle, { flex: 1 })} autoFocus />
                  <button onClick={selectAllVisibleMarkers}
                    style={Object.assign({}, pillBase, { fontSize: '11px', whiteSpace: 'nowrap' })}>{'Select All (' + filteredMarkers.length + ')'}</button>
                  <button onClick={clearSelectedMarkers}
                    style={Object.assign({}, pillBase, { fontSize: '11px', color: RED })}>Clear</button>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '240px', padding: '4px 0' }}>
                  {filteredMarkers.length === 0 && <p style={{ padding: '8px 14px', color: '#999' }}>No markers match your search</p>}
                  {filteredMarkers.slice(0, 500).map(function(m) {
                    return (
                      <label key={m} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 14px', cursor: 'pointer', backgroundColor: selectedMarkers.includes(m) ? ACCENT_LIGHT : 'transparent' }}>
                        <input type="checkbox" checked={selectedMarkers.includes(m)} onChange={() => toggleMarker(m)} style={{ accentColor: ACCENT }} />
                        <span style={{ fontSize: '13px' }}>{m}</span>
                      </label>
                    );
                  })}
                  {filteredMarkers.length > 500 && <p style={{ padding: '6px 14px', color: '#999', fontSize: '12px' }}>Showing first 500 - narrow your search</p>}
                </div>
              </div>
            )}
          </div>

          {/* Cultivar Picker */}
          <div style={sectionStyle} ref={cultivarRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={Object.assign({}, labelStyle, { marginBottom: 0 })}>
                {'🌱 Cultivars (Samples) '}
                <span style={{ fontWeight: 400, color: '#666', fontSize: '13px' }}>
                  {selectedCultivars.length > 0 ? selectedCultivars.length + ' selected' : allCultivars.length + ' available - leave empty for all'}
                </span>
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {selectedCultivars.length > 0 && (
                  <button onClick={clearSelectedCultivars}
                    style={Object.assign({}, pillBase, { fontSize: '11px', color: RED })}>{'Clear (' + selectedCultivars.length + ')'}</button>
                )}
                <button onClick={() => setCultivarDropdownOpen(!cultivarDropdownOpen)}
                  style={Object.assign({}, pillBase, { backgroundColor: ACCENT_LIGHT, color: ACCENT, border: '1px solid ' + ACCENT })}>
                  {cultivarDropdownOpen ? 'Close' : 'Select Cultivars'}
                </button>
              </div>
            </div>

            {selectedCultivars.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                {selectedCultivars.slice(0, 20).map(function(c) {
                  return (
                    <span key={c} style={Object.assign({}, pillBase, { backgroundColor: '#e8f4fd', color: BLUE, border: '1px solid ' + BLUE, display: 'inline-flex', alignItems: 'center', gap: '4px' })}>
                      {c} <span onClick={() => toggleCultivar(c)} style={{ cursor: 'pointer', fontWeight: 700 }}>x</span>
                    </span>
                  );
                })}
                {selectedCultivars.length > 20 && <span style={Object.assign({}, pillBase, { backgroundColor: '#eee' })}>+{selectedCultivars.length - 20} more</span>}
              </div>
            )}

            {cultivarDropdownOpen && (
              <div style={{ border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', maxHeight: '350px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #eee', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="text" value={cultivarSearch} onChange={function(e) { setCultivarSearch(e.target.value); }}
                    placeholder="Search cultivars..." style={Object.assign({}, inputStyle, { flex: 1 })} autoFocus />
                  <button onClick={selectAllVisibleCultivars}
                    style={Object.assign({}, pillBase, { fontSize: '11px', whiteSpace: 'nowrap' })}>{'Select All (' + filteredCultivars.length + ')'}</button>
                  <button onClick={clearSelectedCultivars}
                    style={Object.assign({}, pillBase, { fontSize: '11px', color: RED })}>Clear</button>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '280px', padding: '4px 0' }}>
                  {filteredCultivars.length === 0 && <p style={{ padding: '8px 14px', color: '#999' }}>No cultivars match your filters</p>}
                  {filteredCultivars.slice(0, 500).map(function(c) {
                    var bgColor = selectedCultivars.includes(c.genovix_name) ? '#e8f4fd' : 'transparent';
                    var radBg = c.rad === '2' ? '#e8f4fd' : c.rad === '6' ? '#fff3e0' : '#f5f5f5';
                    var radColor = c.rad === '2' ? BLUE : c.rad === '6' ? ORANGE : GRAY;
                    return (
                      <label key={c.genovix_name} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 14px', cursor: 'pointer', backgroundColor: bgColor }}>
                        <input type="checkbox" checked={selectedCultivars.includes(c.genovix_name)} onChange={() => toggleCultivar(c.genovix_name)} style={{ accentColor: BLUE }} />
                        <span style={{ fontSize: '13px', flex: 1 }}>
                          <strong>{c.genovix_name}</strong>
                          {c.alias_name && <span style={{ color: '#888', marginLeft: '6px' }}>{'(' + c.alias_name + ')'}</span>}
                        </span>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: radBg, color: radColor }}>
                          {c.rad + '-row | ' + c.year}
                        </span>
                      </label>
                    );
                  })}
                  {filteredCultivars.length > 500 && <p style={{ padding: '6px 14px', color: '#999', fontSize: '12px' }}>Showing first 500 - narrow your search</p>}
                </div>
              </div>
            )}
          </div>

          {/* Active Filters Summary */}
          {activeFilterCount() > 0 && (
            <div style={{ marginBottom: '16px', padding: '8px 16px', backgroundColor: '#e8f4fd', borderRadius: '8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <strong>Active Filters:</strong>
              {radFilter && <span style={Object.assign({}, pillBase, { backgroundColor: radFilter === '2' ? BLUE : ORANGE, color: '#fff', border: 'none', padding: '3px 10px' })}>{radFilter}-Row</span>}
              {yearFilter && <span style={Object.assign({}, pillBase, { backgroundColor: GRAY, color: '#fff', border: 'none', padding: '3px 10px' })}>{'Year: ' + yearFilter}</span>}
              {nameSearch.trim() && <span style={Object.assign({}, pillBase, { backgroundColor: GRAY, color: '#fff', border: 'none', padding: '3px 10px' })}>{'Name: "' + nameSearch + '"'}</span>}
              {selectedMarkers.length > 0 && <span style={Object.assign({}, pillBase, { backgroundColor: ACCENT, color: '#fff', border: 'none', padding: '3px 10px' })}>{selectedMarkers.length + ' markers'}</span>}
              {selectedCultivars.length > 0 && <span style={Object.assign({}, pillBase, { backgroundColor: BLUE, color: '#fff', border: 'none', padding: '3px 10px' })}>{selectedCultivars.length + ' cultivars'}</span>}
              <button onClick={() => clearAllFilters(false)} style={Object.assign({}, pillBase, { fontSize: '11px', color: RED, marginLeft: '8px' })}>Clear All</button>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button onClick={handlePreview} disabled={loading}
              style={Object.assign({}, btnPrimary, { backgroundColor: loading ? GRAY : ACCENT })}>
              {loading ? 'Loading Preview...' : 'Preview Subset'}
            </button>
            <button onClick={handleDownload} disabled={downloading}
              style={Object.assign({}, btnPrimary, { backgroundColor: downloading ? GRAY : BLUE })}>
              {downloading ? 'Downloading...' : 'Download CSV'}
            </button>
            <button onClick={() => clearAllFilters(false)}
              style={Object.assign({}, btnPrimary, { backgroundColor: '#fff', color: GRAY, border: '1px solid ' + GRAY })}>
              Clear All Filters
            </button>
            <button onClick={() => setShowFileUpload(!showFileUpload)}
              style={Object.assign({}, btnPrimary, { backgroundColor: '#fff', color: ACCENT, border: '1px solid ' + ACCENT })}>
              {showFileUpload ? 'Hide File Upload' : 'Upload Marker / Cultivar Files'}
            </button>
          </div>

          {/* Preview Table */}
          {preview && (
            <div style={{ marginTop: '10px', marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0 }}>
                  {'Preview '}
                  <span style={{ fontWeight: 400, fontSize: '14px', color: '#666' }}>
                    {preview.showing_rows + ' of ' + preview.total_samples + ' samples | ' + preview.markers_selected + ' markers'}
                    {preview.markers_displayed < preview.markers_selected && ' (showing ' + preview.markers_displayed + ' cols)'}
                  </span>
                </h3>
                <button onClick={handleDownload} disabled={downloading}
                  style={Object.assign({}, btnPrimary, { backgroundColor: downloading ? GRAY : BLUE, padding: '8px 18px', fontSize: '13px' })}>
                  {downloading ? '...' : 'Download All ' + preview.total_samples + ' x ' + preview.markers_selected}
                </button>
              </div>

              <div style={{ overflowX: 'auto', maxHeight: '500px', border: '1px solid #ddd', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: ACCENT, color: '#fff', position: 'sticky', top: 0, zIndex: 2 }}>
                      {preview.columns.map(function(col, i) {
                        return (
                          <th key={i} style={{
                            padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap',
                            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.2)' : 'none',
                            position: i < 4 ? 'sticky' : 'static',
                            left: i < 4 ? (i * 120) + 'px' : 'auto',
                            backgroundColor: ACCENT, zIndex: i < 4 ? 3 : 2,
                          }}>
                            {col}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.map(function(row, ri) {
                      return (
                        <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#f8f9fa' : '#fff' }}>
                          {preview.columns.map(function(col, ci) {
                            return (
                              <td key={ci} style={{
                                padding: '6px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid #eee',
                                borderRight: ci < 3 ? '1px solid #eee' : 'none',
                                position: ci < 4 ? 'sticky' : 'static',
                                left: ci < 4 ? (ci * 120) + 'px' : 'auto',
                                backgroundColor: ci < 4 ? (ri % 2 === 0 ? '#f0f7f6' : '#e8f4f2') : 'inherit',
                                zIndex: ci < 4 ? 1 : 0, fontWeight: ci === 0 ? 600 : 400,
                              }}>
                                {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </React.Fragment>
      )}

      {/* Loading indicator while metadata loads */}
      {selectedTable && metaLoading && (
        <div style={Object.assign({}, sectionStyle, { textAlign: 'center', padding: '40px 20px' })}>
          <p style={{ color: '#666', fontSize: '15px' }}>⏳ Loading table metadata...</p>
        </div>
      )}

      {/* Legacy file upload section — only if metadata finished loading and table is NOT sample-oriented */}
      {(showFileUpload || (selectedTable && !isSampleOriented && !metaLoading)) && selectedTable && (
        <div style={Object.assign({}, sectionStyle, { borderColor: '#b8daff', backgroundColor: '#f0f7ff' })}>
          <h4 style={{ marginTop: 0, color: '#004085' }}>Upload Marker / Cultivar List Files</h4>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
            Upload CSV/Excel files with marker or cultivar names to retrieve a specific subset.
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label style={labelStyle}>Markers <span style={{ fontWeight: 400, color: '#666', fontSize: '12px' }}>(optional)</span></label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input id="marker-file-input" type="file" accept=".csv,.xlsx,.xls"
                  onChange={function(e) { var f = e.target.files[0]; if (f && f.name.match(/\.(csv|xlsx?)$/i)) { setMarkerFile(f); setErrorMessage(''); } else if (f) { setErrorMessage('Use CSV or Excel file'); e.target.value = null; } }}
                  style={Object.assign({}, inputStyle, { padding: '6px' })} />
                {markerFile && <button onClick={() => { setMarkerFile(null); document.getElementById('marker-file-input').value = ''; }} style={Object.assign({}, pillBase, { color: RED, fontSize: '11px' })}>X</button>}
              </div>
              {markerFile && <p style={{ color: '#28a745', fontSize: '12px', marginTop: '4px' }}>{markerFile.name}</p>}
            </div>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label style={labelStyle}>Cultivars <span style={{ fontWeight: 400, color: '#666', fontSize: '12px' }}>(optional)</span></label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input id="cultivar-file-input" type="file" accept=".csv,.xlsx,.xls"
                  onChange={function(e) { var f = e.target.files[0]; if (f && f.name.match(/\.(csv|xlsx?)$/i)) { setCultivarFile(f); setErrorMessage(''); } else if (f) { setErrorMessage('Use CSV or Excel file'); e.target.value = null; } }}
                  style={Object.assign({}, inputStyle, { padding: '6px' })} />
                {cultivarFile && <button onClick={() => { setCultivarFile(null); document.getElementById('cultivar-file-input').value = ''; }} style={Object.assign({}, pillBase, { color: RED, fontSize: '11px' })}>X</button>}
              </div>
              {cultivarFile && <p style={{ color: '#28a745', fontSize: '12px', marginTop: '4px' }}>{cultivarFile.name}</p>}
            </div>
          </div>
          <button onClick={handleLegacyRetrieve} disabled={retrieving}
            style={Object.assign({}, btnPrimary, { backgroundColor: retrieving ? GRAY : '#004085', width: '100%' })}>
            {retrieving ? 'Retrieving...' : 'Retrieve Subset'}
          </button>
        </div>
      )}

      {/* Legacy results */}
      {legacyResults && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Retrieved Subset</h3>
            {legacyResults.data && legacyResults.data.length > 0 && (
              <button onClick={exportLegacyCSV} style={Object.assign({}, btnPrimary, { backgroundColor: ACCENT, padding: '8px 18px', fontSize: '13px' })}>Export CSV</button>
            )}
          </div>
          <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '16px', border: '1px solid #ddd' }}>
            <p><strong>Table:</strong> {legacyResults.table_name}</p>
            <p><strong>Markers Found:</strong> {legacyResults.markers_found} / {legacyResults.total_markers_requested}</p>
            <p><strong>Cultivars Found:</strong> {legacyResults.cultivars_found} / {legacyResults.total_cultivars_requested}</p>
          </div>
          {legacyResults.data && legacyResults.data.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: '400px', border: '1px solid #ddd', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: ACCENT, color: '#fff', position: 'sticky', top: 0 }}>
                    {legacyResults.columns.map(function(col, i) { return <th key={i} style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>; })}
                  </tr>
                </thead>
                <tbody>
                  {legacyResults.data.map(function(row, ri) {
                    return (
                      <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#f8f9fa' : '#fff' }}>
                        {legacyResults.columns.map(function(col, ci) {
                          return <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{row[col] != null ? String(row[col]) : '-'}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ padding: '8px 12px', color: '#666', fontSize: '12px' }}>{legacyResults.data.length + ' rows x ' + legacyResults.columns.length + ' columns'}</p>
            </div>
          )}
        </div>
      )}

      {/* Tips */}
      {selectedTable && isSampleOriented && (
        <div style={{ marginTop: '30px', padding: '14px 20px', backgroundColor: ACCENT_LIGHT, borderRadius: '8px', border: '1px solid #8bcec0' }}>
          <h4 style={{ marginTop: 0, color: ACCENT }}>Tips</h4>
          <ul style={{ marginBottom: 0, color: ACCENT, fontSize: '13px', lineHeight: '1.8' }}>
            <li><strong>Filter by row type:</strong> Click 2-Row or 6-Row to filter barley type</li>
            <li><strong>Select markers:</strong> Use the marker picker to choose specific SNP columns (BK_01, BOPA1_100, etc.)</li>
            <li><strong>Select cultivars:</strong> Pick individual cultivar samples by genovix name</li>
            <li><strong>Preview:</strong> Shows first 50 rows and 50 markers for quick inspection</li>
            <li><strong>Download:</strong> Downloads ALL filtered data as semicolon-delimited CSV</li>
            <li><strong>Combine filters:</strong> Use rad + year + name + markers + cultivars together</li>
            <li><strong>File upload:</strong> Click "Upload Marker / Cultivar Files" for batch selection via CSV</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default PhenotypeSearch;
